import { ButtonHTMLAttributes, ComponentProps, createRef, FC, forwardRef, MouseEvent } from 'react';
import { handleRefSplit, isNil, Size, useComponentProps, Variable } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { ButtonVariant } from '@optimus-web/local-tokens';
import { Icon } from '../Icon';
import { ButtonLoader } from './ButtonLoader';
import { ButtonElement, CounterElement, LoaderWrapper } from './style';

type FormatCounter = (counter: number) => string;

interface ButtonBaseProps extends Variable<ButtonVariant> {
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    icon?: IconName;
    counter?: number;
    formatCounter?: FormatCounter;
    forwardedRef?: React.Ref<HTMLButtonElement>;
    'data-test-id'?: string;
    iconSize?: 'small' | 'medium';
    asTag?: string;
    size?: Size;
    wide?: boolean;
    iconPosition?: 'left' | 'right';
    loading?: boolean;
}

export type ButtonProps = ButtonBaseProps & ButtonHTMLAttributes<HTMLButtonElement>;

const Button = ({
    asTag = 'button',
    children,
    counter,
    disabled,
    formatCounter,
    icon,
    iconPosition = 'left',
    onClick,
    size: customSize,
    variant = 'tertiary',
    iconSize = 'small',
    wide = false,
    forwardedRef,
    loading,
    ...other
}: ButtonProps) => {
    const button = createRef<HTMLButtonElement>();
    const { size } = useComponentProps('Button', { size: customSize });

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
            onClick(event);
        }
    };

    const iconElement = !!icon && <Icon size={iconSize} value={icon} />;

    const counterBadge = !isNil(counter) ? (
        <Counter counter={counter} formatCounter={formatCounter} variant={variant} />
    ) : null;

    const buttonProps: ComponentProps<typeof ButtonElement> = {
        ref: handleRefSplit({ ownRef: button, forwardedRef }),
        size,
        variant,
        onClick: handleClick,
        disabled: loading || disabled,
        wide,
        loading,
        ...other,
    };

    return (
        <ButtonElement as={asTag} {...buttonProps}>
            {loading ? (
                <LoaderWrapper>
                    <ButtonLoader variant={variant} />
                </LoaderWrapper>
            ) : null}
            {iconPosition === 'left' ? iconElement : null}
            {typeof children === 'string' ? <span>{children}</span> : children}
            {counterBadge}
            {iconPosition === 'right' ? iconElement : null}
        </ButtonElement>
    );
};

export default forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => <Button {...props} forwardedRef={ref} />);

export const createLimitFormatter = (limit?: number) => (counter: number) =>
    !isNil(limit) && counter > limit ? `${limit}+` : `${counter}`;

interface CounterProps extends Variable<ButtonVariant> {
    counter: number;
    formatCounter?: FormatCounter;
}

export const Counter: FC<CounterProps> = ({ counter, formatCounter = createLimitFormatter(99), variant }) => (
    <CounterElement variant={variant}>{formatCounter(counter)}</CounterElement>
);
